import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { trackVirtualPageView } from '../services/tracking';

/**
 * Route that only redirects to the canonical entry. It never produces a virtual page view of its
 * own: when the visitor lands on it, the injected snippets already reported the load while the URL
 * was still "/".
 */
const REDIRECT_ONLY_PATH = '/';

/**
 * Emits one virtual page view per SPA navigation.
 *
 * The initial load is already reported by the tracking snippets injected server side, so the first
 * location is marked as handled on mount. That initial report carries the landing URL, which is
 * "/" for a visitor who lands on the root, not the "/product" it redirects to. `location.key` is
 * stable per history entry, which keeps the StrictMode effect replay from duplicating events and
 * still emits when the user navigates back to the entry the app started on.
 */
const usePageViewTracking = (): void => {
  const location = useLocation();

  const lastHandledKey = useRef(location.key);
  const lastPathname = useRef(location.pathname);
  const hasHandledNavigation = useRef(false);

  useEffect(() => {
    if (lastHandledKey.current === location.key) return;

    // Booting on "/" immediately replaces the entry with the canonical route, and that transition
    // is the very page view the boot snippets already reported.
    const isBootRedirect =
      !hasHandledNavigation.current && lastPathname.current === REDIRECT_ONLY_PATH;

    lastHandledKey.current = location.key;
    lastPathname.current = location.pathname;
    hasHandledNavigation.current = true;

    if (isBootRedirect || location.pathname === REDIRECT_ONLY_PATH) return;

    trackVirtualPageView({
      pathname: location.pathname,
      search: location.search,
      title: document.title,
    });
  }, [location.key, location.pathname, location.search]);
};

export default usePageViewTracking;
